import { alpha, createTheme, type TypographyClassKey } from "@mui/material";
import { blueGrey } from "@mui/material/colors";

type Defaults = {
  COLOR: string;
  TYPOGRAPHIES: {
    BLUE_GREYS_700: TypographyClassKey[];
    MAINS: TypographyClassKey[];
  };
};

const DEFAULTS: Defaults = {
  COLOR: "#009cbc",
  TYPOGRAPHIES: {
    BLUE_GREYS_700: ["body1", "body2", "subtitle1", "subtitle2"],
    MAINS: ["h1", "h2", "h3", "h4", "h5", "h6"],
  },
};

// First theme declaration for reusage
let theme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: "thin",
          scrollbarColor: "#78909c #fafafa1a",
        },
        "*": {
          scrollbarColor: "#78909c #fafafa1a",
        },
        "*::-webkit-scrollbar": {
          width: 12, // Aumentado de 6-8 para 12
          height: 12,
        },
        "*::-webkit-scrollbar-track": {
          background: "#cfd8dc", // blueGrey[100] - mais escuro
          borderRadius: 6,
        },
        "*::-webkit-scrollbar-thumb": {
          background: "#78909c", // blueGrey[400] - mais escuro
          borderRadius: 6,
          transition: "background 0.3s ease",
          "&:hover": {
            background: "#607d8b", // blueGrey[500] - mais escuro
          },
        },
        // Remove setas (buttons) da scrollbar
        "*::-webkit-scrollbar-button": {
          display: "none",
        },
        "*::-webkit-scrollbar-corner": {
          background: "#cfd8dc",
        },
        ".chips-scroll": {
          display: "flex",
          gap: "4px",
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: blueGrey[800],
          "& .MuiInputBase-input": {
            color: blueGrey[800],
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: blueGrey[800],
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: blueGrey[800],
          "& .MuiOutlinedInput-input": {
            color: blueGrey[800],
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          color: blueGrey[800],
          "& .MuiFilledInput-input": {
            color: blueGrey[800],
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          pointerEvents: "none",
          userSelect: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        ...Object.fromEntries(
          DEFAULTS.TYPOGRAPHIES.BLUE_GREYS_700.map((key) => [
            key,
            { color: blueGrey[700], userSelect: "none" },
          ]),
        ),
        ...Object.fromEntries(
          DEFAULTS.TYPOGRAPHIES.MAINS.map((key) => [
            key,
            { color: DEFAULTS.COLOR, userSelect: "none" },
          ]),
        ),
        caption: {
          color: blueGrey[600],
          userSelect: "none",
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          "::marker": { color: blueGrey[600] },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: "#0071bc",
          textDecorationColor: "#0071bc",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          cursor: ownerState.readOnly ? "default" : "pointer",
          "& .MuiInputBase-root": {
            cursor: ownerState.readOnly ? "default" : "text",
            "& .MuiInputBase-input": {
              color: blueGrey[800],
              fontSize: ownerState.multiline ? "0.875rem" : "1rem",
            },
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          cursor: ownerState.readOnly ? "default" : "pointer",
          "& .MuiInputBase-root": {
            cursor: ownerState.readOnly ? "default" : "text",
          },
        }),
      },
      variants: [
        {
          props: { size: "xs" },
          style: {
            fontSize: "0.8rem",
            "& .MuiSelect-select": {
              padding: "8px 8px",
              paddingRight: "24px !important",
              minHeight: "auto",
            },
            "& .MuiSelect-icon": {
              fontSize: "1.2rem",
              right: "4px",
            },
          },
        },
      ],
    },
    MuiChip: {
      styleOverrides: {
        label: {
          userSelect: "none",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: blueGrey[700],
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          userSelect: "none",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          maxHeight: 160,
          maxWidth: 200,
        },
      },
    },
  },
  gradient: {
    main: "linear-gradient(to right, #0071bc, #045a94)",
  },
});

// Theme augmentation
theme = createTheme(theme, {
  palette: {
    raip: theme.palette.augmentColor({
      color: {
        main: DEFAULTS.COLOR,
      },
      name: "raip",
    }),
  },
  components: {
    MuiButton: {
      variants: [
        {
          props: { color: "default" },
          style: {
            color: theme.palette.mode === "dark" ? blueGrey[900] : "#FAFAFA",
            backgroundColor:
              theme.palette.mode === "dark" ? "#FAFAFA" : blueGrey[900],
            fontWeight: 700,
            "&:hover": {
              backgroundColor: alpha(
                theme.palette.mode === "dark" ? "#FAFAFA" : "#333",
                0.98,
              ),
            },
          },
        },
      ],
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: theme.palette.background.paper,
          color: blueGrey[800],
          fontSize: theme.typography.body2.fontSize,
          fontWeight: 500,
          borderRadius: "6px",
          padding: "8px 12px",
          boxShadow: theme.shadows[3],
          border: `1px solid ${blueGrey[100]}`,
          userSelect: "none",
        },
        arrow: {
          color: theme.palette.background.paper,
          filter: "drop-shadow(0px 1px 1px rgba(0,0,0,0.15))",
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: blueGrey[900],
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: blueGrey[900],
          color: theme.palette.background.paper,
          fontWeight: 700,
          fontSize: "0.8125rem",
          paddingBlock: "6px",
          paddingInline: "12px",
          borderBottom: "none",
          userSelect: "none",
        },
        stickyHeader: {
          backgroundColor: blueGrey[900],
        },
        body: {
          color: blueGrey[700],
          fontSize: "0.875rem",
          paddingBlock: "4px",
          paddingInline: "12px",
        },
        sizeSmall: {
          paddingBlock: "4px",
          paddingInline: "8px",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-child td, &:last-child th": { border: 0 },
          "&.MuiTableRow-hover:hover": {
            backgroundColor: blueGrey[50],
          },
          "&.Mui-selected": {
            backgroundColor: alpha(blueGrey[200], 0.25),
            "&:hover": {
              backgroundColor: alpha(blueGrey[200], 0.35),
            },
          },
        },
      },
    },
  },
});

declare module "@mui/material/Select" {
  interface SelectPropsSizeOverrides {
    xs: true;
  }
}

declare module "@mui/material/OutlinedInput" {
  interface OutlinedInputPropsSizeOverrides {
    xs: true;
  }
}

declare module "@mui/material/InputBase" {
  interface InputBasePropsSizeOverrides {
    xs: true;
  }
}

declare module "@mui/material/FormControl" {
  interface FormControlPropsSizeOverrides {
    xs: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    default: true;
  }
}

declare module "@mui/material/styles" {
  interface Theme {
    gradient: {
      main: string;
    };
  }
  interface ThemeOptions {
    gradient?: {
      main: string;
    };
  }
  interface Palette {
    raip: Palette["primary"];
  }
  interface PaletteOptions {
    raip?: PaletteOptions["primary"];
  }
}

export default theme;
